import { OPEN_ENDED_FPS } from '../animation/clock';
import type { SessionTiming } from '../av/sessionClock';
import { MAX_REVIEW_FRAMES } from './schema';

/**
 * Visual review timestamps (Phase 5C.1). Pure and deterministic.
 *
 * Review frames sit on a frame grid: the animation's fps, or 30 fps for a sync session
 * without an animation (the session clock's open-ended frame counter). A timestamp is the
 * start of a grid frame, `k / fps`, the same instant a video export at that frame rate
 * draws frame k, so a review frame and an export frame of that instant are the same image.
 */

export { MAX_REVIEW_FRAMES };
export const DEFAULT_REVIEW_FRAMES = 6;

export interface FrameGrid {
  /** Length of the source in seconds (session or animation). */
  durationSec: number;
  fps: number;
}

export class ReviewRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReviewRequestError';
  }
}

export const gridOf = (timing: Pick<SessionTiming, 'durationSec' | 'animation'>): FrameGrid => ({ durationSec: timing.durationSec, fps: timing.animation?.fps ?? OPEN_ENDED_FPS });

/** Number of whole grid frames that start before the end of the source. */
export const gridFrames = (grid: FrameGrid): number => Math.max(1, Math.ceil(grid.durationSec * grid.fps - 1e-6));

/** The grid frame a time falls in (the same flooring the playback clocks use). */
export const gridFrameAt = (seconds: number, fps: number): number => Math.floor(Math.max(0, seconds) * fps + 1e-6);

/** Seconds of the start of grid frame `k`. */
export const gridSeconds = (k: number, fps: number): number => k / fps;

/**
 * `count` frames spread evenly over the source: the middle of each of `count` equal parts,
 * snapped down to the start of its grid frame.
 */
export function evenTimestamps(grid: FrameGrid, count: number): number[] {
  if (!Number.isInteger(count) || count < 1 || count > MAX_REVIEW_FRAMES) throw new ReviewRequestError(`Choose between 1 and ${MAX_REVIEW_FRAMES} frames.`);
  const total = gridFrames(grid);
  if (count > total) throw new ReviewRequestError(`This source has only ${total} frame${total === 1 ? '' : 's'}; choose ${total} or fewer.`);
  const frames = Array.from({ length: count }, (_, i) => Math.min(total - 1, Math.floor(((i + 0.5) / count) * total)));
  return frames.map((k) => gridSeconds(k, grid.fps));
}

/**
 * Parses custom times typed by the user ("0.5, 2.75, 6" seconds, or "m:ss.s"), snaps each
 * to the start of its grid frame and returns them in ascending order. Refuses anything it
 * cannot represent exactly: non-numbers, times outside the source, two times on the same
 * frame, and more than the maximum.
 */
export function parseCustomTimes(text: string, grid: FrameGrid): number[] {
  const parts = text.split(/[\s,;]+/).filter(Boolean);
  if (parts.length === 0) throw new ReviewRequestError('Enter at least one time, in seconds (for example 0.5, 2, 4.25).');
  if (parts.length > MAX_REVIEW_FRAMES) throw new ReviewRequestError(`At most ${MAX_REVIEW_FRAMES} times (you entered ${parts.length}).`);
  const total = gridFrames(grid);
  const seen = new Map<number, string>();
  for (const part of parts) {
    const seconds = parseTime(part);
    if (seconds === null) throw new ReviewRequestError(`“${part}” is not a time. Use seconds (2.5) or minutes:seconds (1:02.5).`);
    if (seconds < 0 || seconds >= grid.durationSec - 1e-9) throw new ReviewRequestError(`${part} s is outside the source, which lasts ${formatTime(grid.durationSec)}.`);
    const k = Math.min(total - 1, gridFrameAt(seconds, grid.fps));
    const other = seen.get(k);
    if (other !== undefined) throw new ReviewRequestError(`${other} and ${part} fall on the same frame (${formatTime(gridSeconds(k, grid.fps))} at ${grid.fps} fps).`);
    seen.set(k, part);
  }
  return [...seen.keys()].sort((a, b) => a - b).map((k) => gridSeconds(k, grid.fps));
}

function parseTime(text: string): number | null {
  const m = /^(?:(\d+):)?(\d+(?:\.\d+)?)$/.exec(text.trim());
  if (!m) return null;
  const seconds = Number(m[2]) + (m[1] ? Number(m[1]) * 60 : 0);
  return Number.isFinite(seconds) ? seconds : null;
}

/** "0:02.750" */
export function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${m}:${s.toFixed(3).padStart(6, '0')}`;
}
